import { ref, useStore } from "@nuxtjs/composition-api";
import { defineStore } from "pinia";

import { useAxios } from "@/composables";
import { useCartsStore } from "@/store";
import CheckoutApi from "@/store/checkout/-api";
import CartsApi from "@/store/checkout/-carts-api";
import { getRetailerProductsStore } from "@/store/retailer-products";
import logger, { logLevel } from "@/utils/logger";
import { filterRemoveNull, filterRemoveUndefined } from "@/utils/transform/array";
import mapData from "@/utils/transform/map-data";

export const useCheckoutStore = defineStore("checkout", () => {
  const axios = useAxios();
  const store = useStore();
  const retailerProductsStore = getRetailerProductsStore(store);
  const cartsStore = useCartsStore();

  // State
  const checkout = ref<Checkout | null>(null);

  // Actions
  async function createCheckout(params: PostCheckoutParams) {
    try {
      checkout.value = null;
      const api = new CheckoutApi(axios);
      const response = await api.post(params);
      const checkoutData = response.data.checkout;
      const invalidRetailerProductVariantIds = checkoutData.invalidProducts ?? [];

      // If some of the retailer product variants are invalid, update the `availability` parameter to be `false` for the invalid variants.
      if (invalidRetailerProductVariantIds.length > 0) {
        const retailerProducts = Object.values(retailerProductsStore.retailerProducts).filter(filterRemoveUndefined);
        const newRetailerProducts = retailerProducts.map((retailerProduct) => {
          return {
            ...retailerProduct,
            variants: retailerProduct.variants.map((retailerProductVariant) => {
              if (invalidRetailerProductVariantIds.includes(retailerProductVariant.id)) {
                return { ...retailerProductVariant, availability: false };
              }
              return retailerProductVariant;
            }),
          };
        });
        retailerProductsStore.setRetailerProducts({ retailerProducts: mapData("id", newRetailerProducts) });
        cartsStore.showCheckoutError();
      }
      // Else, set the checkout object if a checkout url exists.
      else {
        // If the checkout URL does not exist, throw an error.
        if (!checkoutData?.checkoutUrl) {
          throw new Error("Error the checkout does not have a valid checkout url");
        }
        checkout.value = checkoutData;
      }
    } catch (error) {
      logger(logLevel.ERROR, "Error creating a checkout in the checkout service", {}, error);
      cartsStore.showCheckoutError();
    }
  }

  async function createCartCheckout(params: PostCartCheckoutParams) {
    try {
      const api = new CartsApi(axios);
      const response = await api.postCheckout(params);
      const checkoutData = response.data.checkout;
      const invalidProductsVariantIds = checkoutData.invalidProducts ?? [];

      // If some of the cart items are invalid, update the `availability` parameter to be `false`.
      if (invalidProductsVariantIds.length > 0) {
        let retailerProductVariant: RetailerProductVariant | undefined;
        const newRetailerProductVariants = invalidProductsVariantIds
          .map((retailerProductVariantId) => {
            retailerProductVariant = cartsStore.retailerProductVariants[retailerProductVariantId];
            if (retailerProductVariant) {
              return { ...retailerProductVariant, availability: false };
            }
            return null;
          })
          .filter(filterRemoveNull);
        cartsStore.retailerProductVariants = Object.assign(
          {},
          cartsStore.retailerProductVariants,
          mapData("id", newRetailerProductVariants),
        );
      }
      // Else, set the checkout object in the checkout store if a checkout url exists.
      else {
        // If the checkout URL does not exist, throw an error.
        if (!checkoutData?.checkoutUrl) {
          throw new Error("Error the cart checkout does not have a valid checkout url");
        }
        checkout.value = checkoutData;
      }
    } catch (error) {
      logger(logLevel.ERROR, "Error attemping to checkout a cart from the cart service", { error });
      cartsStore.showCheckoutError();
    }
  }

  return {
    checkout,
    createCheckout,
    createCartCheckout,
  };
});
